import React, { useEffect, useState } from "react";
import * as PR from "../../../../prime-react/index";
import { RAW_FILTER_DATA } from "../../../../shared/common";
import { isEmpty, some } from "lodash";
import { currencyFormatAbbreviated, formatShortDate, predefinedRanges } from "../../../../utils/reuse";
import SummarySearchFilter from "./SummarySearchFilter";
import SummaryAppliedFilter from "./SummaryAppliedFilter";
import { storeAlertPlatFormFilters, storeAnomalieFilters, storeBaselineFilters, storePlayerFilters } from "../../../../store/filter.slice";
import { useDispatch } from "react-redux";
import { maxDate } from "../graph-and-stats/GraphConfig";
import { DateRangePicker } from 'rsuite';
import "./SearchSummary.scss";
import "./rsuite.scss";
import BettingMenuBar from "../../../../layouts/BettingMenuBar";
import { useNavigate } from "react-router-dom";
import { ErrorMessage, useToast } from "../../../../context/ToastContext";

const SearchAndTotalSummary = ({ searchRequestHandler, totalData, searchOptions, loading, alertPlatformState, defaultDate, defaultGroup, filterHandler, requestType, screenType }) => {
  const dispatch = useDispatch();
  const { showToast, clearError } = useToast();

  const [date, setDate] = useState([]);
  const [isSearchModalVisible, setSearchModalVisible] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState(alertPlatformState);
  const [filterVisible, setFilterVisible] = useState(true);

  const [companyOptions, setCompanyOptions] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const [stateOptions, setStateOptions] = useState([]);
  const [selectedStates, setSelectedStates] = useState(null);


  const [propertieOptions, setPropertieOptions] = useState([]);
  const [selectedProperty, setSelectedProperties] = useState(null);
  const navigate = useNavigate();
  const [playerIdSearch, setPlayerIdSearch] = useState('');
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedAlertType, setSelectedAlertType] = useState("all");
  const statusOptions = [
    { label: "All Status", value: "" },
    { label: "Reviewed", value: true },
    { label: "Not Reviewed", value: false }
  ]
  const alertOptions = [
    { label: "All Alerts", value: "all" },
    { label: "Max Bet Alerts", value: "maxbet" },
    { label: "Sudden Spike Alerts", value: "suddenspike" }
  ]
  const baseLineErrorId = 'baseLine-error';

  const [sportOptions, setSportOptions] = useState([]);
  const [selectedSports, setSelectedSports] = useState([]);
  const [tournamentKeywordSearch, setTournamentKeywordSearch] = useState('');
  // Sync date from state
  useEffect(() => {
    setDate(alertPlatformState?.date?.date?.length ? alertPlatformState?.date?.date : defaultDate);
  }, [alertPlatformState?.date?.date, defaultDate]);

  // Sync anomalies status and alert type filters
  useEffect(() => {
    setSelectedStatus(alertPlatformState?.reviewed !== "" ? alertPlatformState.reviewed : "");
    setSelectedAlertType(alertPlatformState?.types ? alertPlatformState.types : "all");
  }, [alertPlatformState?.reviewed, alertPlatformState?.types]);

  // Sync selected properties based on screen type and locations/channel
  useEffect(() => {
    if (screenType === 'player' || screenType === 'anomalies') {
      const findData = propertieOptions.find(c => c.id === 1)
      setSelectedProperties(findData?.id ? findData : null)
    } else if (alertPlatformState.locations && alertPlatformState.locations?.length > 0) {
      const doesOnlineProperty = alertPlatformState.locations.map(l => l.name.toLowerCase()).includes('online');
      if (doesOnlineProperty && alertPlatformState.locations?.length >= 2) {
        setSelectedProperties(propertieOptions.find(c => c.id === 'both'))
      } else if (doesOnlineProperty) {
        const findData = propertieOptions.find(c => c.id === 1)
        setSelectedProperties(findData?.id ? findData : null)
      } else {
        const findData = propertieOptions.find(c => c.id === 2)
        setSelectedProperties(findData?.id ? findData : null)
      }
    } else if (alertPlatformState.channel && alertPlatformState.channel?.length > 0) {
      const properties = alertPlatformState.channel?.length === 2 ? { id: "both", "name": "Online & Retail" } : alertPlatformState.channel[0];
      setSelectedProperties(properties)
    } else {
      setSelectedProperties({ id: "both", "name": "Online & Retail" })
    }
  }, [alertPlatformState.channel, alertPlatformState.locations, propertieOptions, screenType]);

  useEffect(() => {
    if (screenType === 'baseline' && searchOptions?.sport?.length > 0) {
      setSelectedSports(alertPlatformState?.baselineSports || []);
      setTournamentKeywordSearch(alertPlatformState?.tournamentKeywordSearch || '');
    }
  }, [alertPlatformState?.baselineSports, alertPlatformState?.tournamentKeywordSearch, screenType, searchOptions?.sport?.length])

  useEffect(() => {
    const companies = searchOptions?.group?.map(c => ({ label: c.name, value: c.id }));
    const states = searchOptions?.location?.map(s => ({ label: s.name, value: s.id }));
    let channels = [{ id: "both", name: "Online & Retail" }, ...(searchOptions?.channel?.map(c => ({ ...c })) ?? [])];
    setCompanyOptions(companies);
    setStateOptions(states);
    setSportOptions(searchOptions?.sport ?? []);
    setPropertieOptions((screenType === 'player' || screenType === 'anomalies') ? channels.filter(c => c.id === 1) : channels)
    if (companies?.length) {
      setSelectedGroup(defaultGroup?.id);
    }
    if (states?.length) {
      setSelectedStates(states[0].value);
    }
  }, [searchOptions, defaultGroup?.id, screenType]);

  useEffect(() => {
    setSelectedFilters(alertPlatformState)
  }, [alertPlatformState]);

  const setFilters = (searchData) => {
    let data = { ...searchData, playerID: alertPlatformState?.playerID ?? '', types: alertPlatformState?.types ?? 'all', reviewed: alertPlatformState?.reviewed ?? '' };
    if (searchData?.locations?.length) {
      const doesOnlineProperty = searchData?.locations?.map(l => l.name.toLowerCase()).includes('online');
      let channels = []
      if (doesOnlineProperty && searchData.locations?.length >= 2) {
        channels = propertieOptions?.filter(channel => channel.id !== 'both')
      } else if (doesOnlineProperty) {
        channels = propertieOptions?.filter(channel => channel.id === 1)
      } else {
        channels = propertieOptions?.filter(channel => channel.id === 2)
      }
      data = { ...data, channel: channels };
    }
    setSelectedFilters(data);
    if (screenType === 'player') {
      dispatch(storePlayerFilters(data));
    } else if (screenType === 'anomalies') {
      dispatch(storeAnomalieFilters(data));
    } else if (screenType === 'baseline') {
      dispatch(storeBaselineFilters(data));
    } else {
      dispatch(storeAlertPlatFormFilters(data));
    }
    searchRequestHandler(data);
  }
  const filterHideShow = () => {
    setFilterVisible(!filterVisible);
  };

  const globalFilterHandler = (e, type = '') => {
    if (!type) return;

    const updateFiltersAndSearch = (updatedFields) => {
      const filters = { ...alertPlatformState, ...updatedFields };
      setSelectedFilters(filters);
      if (screenType === 'player') {
        dispatch(storePlayerFilters(filters));
      } else if (screenType === 'anomalies') {
        dispatch(storeAnomalieFilters(filters));
      } else if (screenType === 'baseline') {
        dispatch(storeBaselineFilters(filters));
      } else {
        dispatch(storeAlertPlatFormFilters(filters));
      }
      searchRequestHandler(filters);
    };
    switch (type) {
      case 'date': {
        const dateValues = e.value || [];
        const hasTwoDates = dateValues.length === 2;
        const [startDate, endDateRaw] = dateValues;
        const endDate = endDateRaw ?? startDate;
        setDate(dateValues);
        if (hasTwoDates && startDate) {
          const dateRange = `${formatShortDate(startDate)} - ${formatShortDate(endDate)}`;
          updateFiltersAndSearch({ date: { date: dateValues, dateRange } });
        }
        break;
      }
      case 'channel': {
        const value = e.value;
        setSelectedProperties(value);
        const filteredChannels =
          value?.id === 'both'
            ? propertieOptions?.filter(channel => channel.id !== value.id)
            : value ? [value] : [];
        updateFiltersAndSearch({ channel: filteredChannels });
        break;
      }
      case 'groupID': {
        const value = e.value;
        setSelectedGroup(value);
        filterHandler(value, 'group')
        updateFiltersAndSearch({ groupID: value });
        break;
      }
      case 'stateID': {
        const value = e.value;
        setSelectedStates(e.value)
        updateFiltersAndSearch({ stateID: value });
        break;
      }
      case 'sport': {
        const value = e.value || [];
        setSelectedSports(e.value)
        updateFiltersAndSearch({ baselineSports: value, type: 'sport' });
        break;
      }
      case 'tournamentKeywordSearch': {
        const value = e.value;
        updateFiltersAndSearch({ tournamentKeywordSearch: value, type: 'tournament' });
        break;
      }
      case 'reviewed': {
        const value = e.value;
        setSelectedStatus(e.value)
        updateFiltersAndSearch({ reviewed: value });
        break;
      }
      case 'types': {
        const value = e.value;
        setSelectedAlertType(e.value)
        updateFiltersAndSearch({ types: value });
        break;
      }
      default:
        break;
    }
  };

  const normalizeToMidnight = (date) => {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  };
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const titles = document.querySelectorAll('.rs-calendar-header-title');
      const prevButtons = document.querySelectorAll('.rs-calendar-header-backward');
      const nextButtons = document.querySelectorAll('.rs-calendar-header-forward');

      if (titles.length >= 2 && prevButtons.length >= 2) {
        const rightTitle = titles[1]?.textContent;
        const [monthName, year] = rightTitle.split(' ');
        const visibleMonth = new Date(`${monthName} 1, ${year}`);
        const maxMonth = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);

        const rightPrevBtn = prevButtons[1];
        const rightNextBtn = nextButtons[1];

        if (rightNextBtn) {
          if (visibleMonth >= maxMonth) {
            rightNextBtn.style.pointerEvents = 'none';
            rightNextBtn.style.opacity = 0.5;
          } else {
            rightNextBtn.style.pointerEvents = 'auto';
            rightNextBtn.style.opacity = 1;
          }
        }
        if (visibleMonth > maxMonth && rightPrevBtn) {
          setTimeout(() => {
            rightPrevBtn.click();
          }, 100);
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);


  const playerSearchValueHandler = (e, type = '') => {
    e?.preventDefault()
    if (!type) {
      navigate(`/player-analytics/${playerIdSearch}`)
    }
  }
  const inputSearchHandler = (e) => {
    const value = e.target.value?.trim();
    setPlayerIdSearch(value);
  };
  const tournamentSearchHandler = (e) => {
    const value = e.target.value;
    setTournamentKeywordSearch(value);
  };
  const tournamentKeywordSearchHandler = (e, type = '') => {
    e?.preventDefault();
    clearError(baseLineErrorId);
    if (type === 'clear') {
      globalFilterHandler({ value: '' }, 'tournamentKeywordSearch');
    } else {
      globalFilterHandler({ value: tournamentKeywordSearch }, 'tournamentKeywordSearch');
    }
    if (selectedSports?.length === 0) {
      showToast("error", "Please select at least one sport", baseLineErrorId);
      setTimeout(() => { clearError(baseLineErrorId) }, 5000);
    }
  }


  return (
    <>
      <div className="betting-data-block">
        <BettingMenuBar />
      </div>
      <section className="search-summary-section">
        <div className="grid">
          <div className="col-12 md:col-3">
            <PR.Dropdown value={selectedGroup} onChange={(e) => { globalFilterHandler(e, 'groupID') }} options={companyOptions}
              placeholder="Select User" className="users-dropdown" />
          </div>
          <div className="col-12 md:col-9">
            <div className="flex align-items-center justify-content-end custom-gap">
              <div className="multi-select-filed">
                <i className="pi pi-map-marker select-icon"></i>
                <PR.Dropdown value={selectedStates} onChange={(e) => { globalFilterHandler(e, 'stateID') }} options={stateOptions}
                  placeholder="All Locations" className="default-dropdown" />
              </div>
              {(screenType !== 'baseline' && screenType !== 'anomalies') &&
                <div className="multi-select-filed">
                  <i className="pi pi-building select-icon"></i>
                  <PR.Dropdown value={selectedProperty} onChange={(e) => { globalFilterHandler(e, 'channel') }} options={propertieOptions}
                    placeholder="Select Channel" className="default-dropdown" optionLabel="name" disabled={alertPlatformState.locations?.length} />
                </div>
              }
              {screenType === 'anomalies' &&
                <>
                  <div className="multi-select-filed">
                    <i className="pi pi-verified select-icon"></i>
                    <PR.Dropdown value={selectedStatus} onChange={(e) => { globalFilterHandler(e, 'reviewed') }} options={statusOptions}
                      placeholder="Select Status" className="default-dropdown" disabled={alertPlatformState.locations?.length} />
                  </div>
                  <div className="multi-select-filed">
                    <i className="pi pi-bell select-icon"></i>
                    <PR.Dropdown value={selectedAlertType} onChange={(e) => { globalFilterHandler(e, 'types') }} options={alertOptions}
                      placeholder="Select Alert" className="default-dropdown" disabled={alertPlatformState.locations?.length} />
                  </div>
                </>
              }
              {screenType !== 'baseline' &&
                <DateRangePicker
                  ranges={predefinedRanges}
                  placeholder="Select date"
                  value={date?.length === 2 ? date : null}
                  format="dd/MM/yyyy"
                  className={`date_range ${screenType}`}
                  onChange={(value) => {
                    const e = { value };
                    globalFilterHandler(e, 'date');
                  }}
                  shouldDisableDate={(date) => normalizeToMidnight(date) > normalizeToMidnight(maxDate)}
                  cleanable={false}
                  character={' - '}
                  limitEndYear={1} // 1 = current year
                  placement="bottomEnd"
                />
              }
              {screenType === 'baseline' &&
                <>
                  <div className="multi-select-filed baseline-select">
                    <i className="pi pi-filter select-icon"></i>
                    <PR.MultiSelect value={selectedSports} onChange={(e) => { globalFilterHandler(e, 'sport') }} options={sportOptions}
                      placeholder="Select Sports*" className="default-dropdown" optionLabel="name" disabled={loading} showClear={true} filter={true} resetFilterOnHide={true} maxSelectedLabels={2} />
                  </div>

                  <span className="p-input-icon-right">
                    <form onSubmit={tournamentKeywordSearchHandler}>
                      <PR.InputText
                        id="alertLegacyId"
                        placeholder="Search by Tournament"
                        className="search-box pr-7"
                        value={tournamentKeywordSearch}
                        style={{ width: '300px', fontSize: 13, padding: '8px 12px' }}
                        autoComplete="off"
                        onChange={(e) => tournamentSearchHandler(e)}
                      />
                      <div className="error-message mt-1 absolute">
                        <ErrorMessage id={baseLineErrorId} />
                      </div>
                    </form>
                    <span className="right-search-icon">
                      {!!tournamentKeywordSearch && <i style={{ cursor: 'pointer' }} onClick={(e) => { setTournamentKeywordSearch(''); tournamentKeywordSearchHandler(e, 'clear'); }} className="pi pi-times text-sm mr-2" />}
                      <i style={{ cursor: 'pointer' }} className="pi pi-search" />
                    </span>
                  </span>
                </>
              }
              {screenType === 'player' &&
                <span className="p-input-icon-right">
                  <form onSubmit={playerSearchValueHandler}>
                    <PR.InputText
                      id="alertLegacyId"
                      placeholder="Search by PlayerID"
                      className="search-box pr-7"
                      value={playerIdSearch}
                      style={{ width: '300px', fontSize: 13, padding: '8px 12px' }}
                      autoComplete="off"
                      onChange={(e) => inputSearchHandler(e)}
                      keyfilter="int"
                    />
                    <p className="error-message mt-1 absolute"></p>
                  </form>
                  <span className="right-search-icon">
                    {!!playerIdSearch && <i style={{ cursor: 'pointer' }} onClick={(e) => { setPlayerIdSearch(''); playerSearchValueHandler(e, 'clear'); }} className="pi pi-times text-sm mr-2" />}
                    <i style={{ cursor: 'pointer' }} className="pi pi-search" />
                  </span>
                </span>
              }
              {screenType === 'dashboard' &&
                <PR.Button iconPos='left' label="Search Filters" className="filters-button" onClick={() => setSearchModalVisible(true)} />
              }
            </div>
          </div>
        </div>
        {screenType === 'dashboard' &&
          <>
            <h2 className="heading-h2 mt-3">All Bets</h2>
            <div className="grid align-items-center text-center">
              <div className="col-12">
                {(some(selectedFilters, v => !isEmpty(v))) &&
                  <SummaryAppliedFilter
                    selectedFilters={selectedFilters}
                    setFilters={setFilters}
                    filterVisible={filterVisible}
                    filterHideShow={filterHideShow}
                  />
                }
              </div>
              <div className="col-6">
                <div className="info-box">
                  <p>Total Stake</p>
                  <h2>
                    {loading ? (
                      <div>
                        <i className="pi pi-spin pi-spinner"></i>
                      </div>
                    ) :
                      currencyFormatAbbreviated(totalData?.totalStake, defaultGroup?.id)
                    }
                  </h2>
                </div>
              </div>
              <div className="col-6">
                <div className="info-box">
                  <p>Net Gross Gaming Revenue (GGR)</p>
                  <h2>
                    {loading ? (
                      <div>
                        <i className="pi pi-spin pi-spinner"></i>
                      </div>
                    ) : (
                      currencyFormatAbbreviated(totalData?.netGGR, defaultGroup?.id)
                    )}
                  </h2>
                </div>
              </div>
            </div>
          </>
        }
        <PR.Dialog
          visible={isSearchModalVisible}
          onHide={() => setSearchModalVisible(false)}
          blockScroll={true}
          className="response-dialog filter-dialog betting-data-search-dialog"
        >
          {isSearchModalVisible &&
            <SummarySearchFilter
              setFilterDialogVisible={setSearchModalVisible}
              setFilters={setFilters}
              staticOptionData={RAW_FILTER_DATA}
              selectedFilters={selectedFilters}
              screenType={'alertPlatform'}
              groupId={selectedGroup}
              requestType={requestType}
            />
          }
        </PR.Dialog>
      </section>
    </>

  );
};

export default SearchAndTotalSummary;
